import {
  Typography,
  TextField,
  Button,
  Box,
  Paper,
  MenuItem,
  CircularProgress
} from "@mui/material";
import { useState, useEffect, useMemo, useCallback } from "react";
import SendIcon from '@mui/icons-material/Send';
import ContextoValidation from "../../utils/contextoValidation";
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { getReadonlyFlag } from "../../utils/getRoleToken";
import ImageIcon from '@mui/icons-material/Image';
import GaleriaImagensProduto from './GaleriaImagensProduto';
import ModalCadastroProduto from "./ModalCadastroProduto";
import AddBusinessOutlinedIcon from '@mui/icons-material/AddBusinessOutlined';

const ContextoProduto = ({
  nomeContexto = '',
  conteudoContexto = null,
  setDeveUpdateContexto = () => { },
  produto = 'AC',
  nomeExibicao = 'Informações adicionais' // Nova prop com valor padrão
}) => {
  const [contexto, setContexto] = useState(conteudoContexto || "");
  const [contextoOriginal, setContextoOriginal] = useState(conteudoContexto || "");
  const [validationModalOpen, setValidationModalOpen] = useState(false);
  const [produtoSelecionado, setProdutoSelecionado] = useState('');
  const [produtos, setProdutos] = useState([]);
  const [isLoadingProdutos, setIsLoadingProdutos] = useState(false);
  const [isLoadingContexto, setIsLoadingContexto] = useState(false);
  const [galeriaOpen, setGaleriaOpen] = useState(false);
  const [cadastroProdutoOpen, setCadastroProdutoOpen] = useState(false);
  const [cadastroSucesso, setCadastroSucesso] = useState(new Set());
  const [cadastroEmAndamento, setCadastroEmAndamento] = useState(new Set());
  const [cadastroLoading, setCadastroLoading] = useState(false);
  const [cadastroError, setCadastroError] = useState("");
  const [empreendimentosCvIds, setEmpreendimentosCvIds] = useState(new Set());
  const [empreendimentosCv, setEmpreendimentosCv] = useState([]);
  const [idCrm, setIdCrm] = useState(null);

  // URL base da API
  const baseURL = process.env.REACT_APP_API_BASE_URL;

  const readonly = getReadonlyFlag();
  const canCadastrarProduto = !readonly;

  const CONTROL_H = 40; // px
  const primaryButtonSx = {
    backgroundColor: "#7b3ff6",
    color: "#fff",
    boxShadow: "0 6px 14px rgba(123, 63, 246, 0.25)",
    borderRadius: "999px",
    px: 2.5,
    transition: "background-color 180ms ease, box-shadow 180ms ease, transform 180ms ease",
    "&:hover": {
      backgroundColor: "#6a35e3",
      boxShadow: "0 8px 18px rgba(123, 63, 246, 0.32)",
      transform: "translateY(-1px)",
    },
    "&:disabled": {
      backgroundColor: "#d4c6fb",
      color: "#fff",
      boxShadow: "none",
    },
  };

  // Buscar produtos do backend
  const fetchProdutos = useCallback(async () => {
    try {
      setIsLoadingProdutos(true);

      const token = localStorage.getItem('jwtToken');
      if (!token) {
        console.error("Token não encontrado no localStorage.");
        return;
      }

      const decodedToken = jwtDecode(token);
      const idClienteAipim = decodedToken.id_cliente;

      const params = {
        idClienteAipim: idClienteAipim,
      };

      const response = await axios.get(`${baseURL}/produto/sdr/produtos`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });

      // Filtrar apenas produtos ativos
      const produtosAtivos = response.data.filter(emp => emp.ativo === true);
      setProdutos(produtosAtivos);
    } catch (error) {
      console.error("Erro ao buscar produtos:", error);
      if (error.response?.status === 401 ||
        error.response?.data?.message?.toLowerCase().includes("token")) {
        // Aqui você pode adicionar lógica para lidar com token expirado
        console.error("Token expirado ou inválido");
      }
    } finally {
      setIsLoadingProdutos(false);
    }
  }, [baseURL]);

  useEffect(() => {
    if (baseURL) {
      fetchProdutos();
    }
  }, [baseURL, fetchProdutos]);

  useEffect(() => {
    if (cadastroProdutoOpen && Number(idCrm) === 0) {
      setCadastroSucesso(new Set());
      setCadastroError("");
    }
  }, [cadastroProdutoOpen, idCrm]);

  useEffect(() => {
    const fetchParametrosSdr = async () => {
      try {
        const token = localStorage.getItem('jwtToken');
        if (!token) {
          console.error("Token não encontrado no localStorage.");
          return;
        }

        const decodedToken = jwtDecode(token);
        const idClienteAipim = decodedToken.id_cliente;

        const response = await axios.get(`${baseURL}/produto/sdr/parametros`, {
          params: { idClienteAipim },
          headers: { Authorization: `Bearer ${token}` }
        });

        const nextIdCrm = response.data?.sdrConfig?.idCrm;
        setIdCrm(nextIdCrm ?? null);
      } catch (error) {
        console.error("Erro ao buscar parâmetros SDR:", error);
      }
    };

    if (baseURL) {
      fetchParametrosSdr();
    }
  }, [baseURL]);

  const fetchEmpreendimentosCv = async () => {
    try {
      setCadastroError("");

      const token = localStorage.getItem('jwtToken');
      if (!token) {
        console.error("Token não encontrado no localStorage.");
        return;
      }

      const decodedToken = jwtDecode(token);
      const idClienteAipim = decodedToken.id_cliente;

      const response = await axios.get(`${baseURL}/produto/sdr/empreendimentos/cv`, {
        params: { idClienteAipim },
        headers: { Authorization: `Bearer ${token}` }
      });

      const cvData = Array.isArray(response.data) ? response.data : [];
      const ids = cvData
        .map((item) => item?.idEmpreendimento)
        .filter((id) => id != null)
        .map((id) => String(id));

      setEmpreendimentosCvIds(new Set(ids));
      setEmpreendimentosCv(cvData);
    } catch (error) {
      console.error("Erro ao buscar empreendimentos do CV:", error);
      setCadastroError("Não foi possí­vel carregar os empreendimentos do CV.");
    }
  };

  // Buscar contexto do produto selecionado
  useEffect(() => {
    const fetchContextoProduto = async () => {
      if (!produtoSelecionado) {
        setContexto("");
        setContextoOriginal("");
        return;
      }

      try {
        setIsLoadingContexto(true);

        const token = localStorage.getItem('jwtToken');
        if (!token) {
          console.error("Token não encontrado no localStorage.");
          return;
        }

        const decodedToken = jwtDecode(token);
        const idClienteAipim = decodedToken.id_cliente;

        const params = {
          idClienteAipim: idClienteAipim,
          id: produtoSelecionado
        };

        const response = await axios.get(`${baseURL}/produto/sdr/contexto-produto`, {
          params,
          headers: { Authorization: `Bearer ${token}` }
        });

        const contextoData = response.data?.contexto || "";
        setContexto(contextoData);
        setContextoOriginal(contextoData);

      } catch (error) {
        console.error("Erro ao buscar contexto do produto:", error);
        if (error.response?.status === 401 ||
          error.response?.data?.message?.toLowerCase().includes("token")) {
          console.error("Token expirado ou inválido");
        }
      } finally {
        setIsLoadingContexto(false);
      }
    };

    fetchContextoProduto();
  }, [produtoSelecionado, baseURL]);

  // Verifica se houve alterações
  const hasChanges = contexto !== contextoOriginal;

  // Verifica se deve habilitar o botão
  const shouldEnableButton = () => {
    // Verifica se o contexto atual tem conteúdo
    const hasContent = contexto.length > 0;

    // Se não há conteúdo original, só habilita se tem texto
    if (!contextoOriginal) {
      return hasContent;
    }

    // Se há conteúdo original, só habilita se houve mudanças E tem conteúdo
    return hasChanges && hasContent;
  };

  const handleEnviarContexto = () => {
    // Abre o modal de validação
    setValidationModalOpen(true);
  };

  const handleConfirmContexto = async (dadosAlteracao) => {
    try {
      console.log('Contexto confirmado:', dadosAlteracao);

      // Atualiza o contexto original com o novo valor
      setContextoOriginal(contexto);

    } catch (error) {
      console.error("Erro ao processar confirmação:", error);
    }
  };

  // Função para resetar alterações (opcional)
  const handleResetChanges = () => {
    setContexto(contextoOriginal);
  };

  const handleProdutoChange = (event) => {
    const selectedId = event.target.value;
    setProdutoSelecionado(selectedId);

    // Se você precisar do objeto completo do produto:
    const selectedProduto = produtos.find(emp => emp.id === selectedId);
    console.log('Produto selecionado:', selectedProduto);
  };

  const resolveProdutoId = (produto, index = 0) => {
    return (
      produto?.id ??
      produto?.idProduto ??
      produto?.idEmpreendimentoCrm ??
      index
    );
  };

  const isProdutoCadastrado = (produto) => {
    if (produto?.cadastrado === true) return true;
    const contexto = produto?.contexto ?? produto?.contextoProduto ?? produto?.contexto_produto ?? "";
    if (typeof contexto === "string" && contexto.trim().length > 0) return true;
    if (produto?.idContextoProduto ?? produto?.idContexto) return true;
    return false;
  };

  const produtosCadastrados = useMemo(() => {
    const cadastrados = new Set();
    produtos.forEach((produto, index) => {
      const idProduto = resolveProdutoId(produto, index);
      const idEmpreendimentoCrm = produto?.idEmpreendimentoCrm;
      if (idProduto == null) return;
      if (isProdutoCadastrado(produto)) {
        cadastrados.add(String(idProduto));
      }
      if (
        idEmpreendimentoCrm != null &&
        empreendimentosCvIds.has(String(idEmpreendimentoCrm))
      ) {
        cadastrados.add(String(idProduto));
      }
    });
    return cadastrados;
  }, [produtos, empreendimentosCvIds]);

  const produtosParaCadastro = useMemo(() => {
    if (!empreendimentosCv || empreendimentosCv.length === 0) return produtos;

    const idsCrmExistentes = new Set(
      produtos
        .map((produto) => produto?.idEmpreendimentoCrm)
        .filter((id) => id != null)
        .map((id) => String(id))
    );

    const cvNaoCadastrados = empreendimentosCv
      .filter((item) => item?.idEmpreendimento != null)
      .filter((item) => !idsCrmExistentes.has(String(item.idEmpreendimento)))
      .map((item) => ({
        nome: item?.nome || "-",
        idEmpreendimentoCrm: item.idEmpreendimento,
        origem: "cv",
      }));

    return [...produtos, ...cvNaoCadastrados];
  }, [produtos, empreendimentosCv]);

  const handleCadastrarProduto = async (produto) => {
    setCadastroError("");

    const token = localStorage.getItem('jwtToken');
    if (!token) {
      setCadastroError("Token não encontrado. Faça login novamente.");
      return;
    }

    const decodedToken = jwtDecode(token);
    const idClienteAipim = decodedToken.id_cliente;
    const idUsuario =
      decodedToken?.id_usuario ?? decodedToken?.idUsuario ?? null;
    const nomeDoUsuario =
      decodedToken?.nome ?? decodedToken?.nomeUsuario ?? null;

    if (!idClienteAipim) {
      setCadastroError("Não foi possível identificar o cliente.");
      return;
    }

    const enviarJustificativaCadastro = async (nomeProduto) => {
      try {
        if (!idUsuario || !nomeDoUsuario) {
          throw new Error("Dados do usuario nao encontrados no token.");
        }

        const payloadJustificativa = {
          idClienteAipim: idClienteAipim,
          idUsuario: idUsuario,
          idProduto: 2,
          campo: "Informações do Produto",
          valorNovo: "",
          valorAnterior: "",
          justificativa: `${nomeDoUsuario} | [CADASTROU] | Produto: ${nomeProduto || "-"}`,
        };

        await axios.post(
          `${baseURL}/produto/Justificativa`,
          payloadJustificativa,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
      } catch (error) {
        console.error("Erro ao enviar justificativa de cadastro:", error);
      }
    };

    if (idCrm == null) {
      setCadastroError("Configuracao do CRM ainda nao foi carregada. Tente novamente.");
      return;
    }

    const isCrmManual = Number(idCrm) === 0;

    if (isCrmManual) {
      const nome = String(produto?.nome || "").trim();
      if (!nome) {
        setCadastroError("Informe o nome do produto.");
        return;
      }

      setCadastroLoading(true);

      try {
        await axios.post(
          `${baseURL}/produto/sdr/empreendimentos`,
          {
            nome,
          },
          {
            params: { idClienteAipim },
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        const successKey = `nome:${nome}`;
        setCadastroSucesso((prev) => {
          const next = new Set(prev);
          next.add(successKey);
          return next;
        });

        await enviarJustificativaCadastro(nome);
        await fetchProdutos();
      } catch (error) {
        console.error("Erro ao cadastrar produto:", error);
        const apiMessage = error.response?.data?.message;
        setCadastroError(apiMessage || "Erro ao cadastrar produto. Tente novamente.");
      } finally {
        setCadastroLoading(false);
      }

      return;
    }

    const idEmpreendimentoCrm = produto?.idEmpreendimentoCrm;

    if (idEmpreendimentoCrm == null) {
      setCadastroError("Não foi possível identificar o empreendimento no CRM.");
      return;
    }

    const idProdutoKey = String(idEmpreendimentoCrm);
    if (produtosCadastrados.has(idProdutoKey)) return;
    if (cadastroSucesso.has(idProdutoKey)) return;

    setCadastroEmAndamento((prev) => {
      const next = new Set(prev);
      next.add(idProdutoKey);
      return next;
    });

    try {
      await axios.post(
        `${baseURL}/produto/sdr/empreendimentos`,
        { idEmpreendimentoCrm },
        {
          params: { idClienteAipim },
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setCadastroSucesso((prev) => {
        const next = new Set(prev);
        next.add(idProdutoKey);
        return next;
      });

      const nomeProduto = produto?.nome;
      await enviarJustificativaCadastro(nomeProduto);
      await fetchProdutos();
    } catch (error) {
      console.error("Erro ao cadastrar produto:", error);
      const apiMessage = error.response?.data?.message;
      setCadastroError(apiMessage || "Erro ao cadastrar produto. Tente novamente.");
    } finally {
      setCadastroEmAndamento((prev) => {
        const next = new Set(prev);
        next.delete(idProdutoKey);
        return next;
      });
    }
  };

  const handleOpenCadastroProduto = () => {
    setCadastroProdutoOpen(true);
    fetchEmpreendimentosCv();
  };

  const handleCloseCadastroProduto = () => {
    setCadastroProdutoOpen(false);
    setCadastroError("");
  };

  return (
    <>
      <Paper
        elevation={2}
        sx={{
          p: { xs: 2, sm: 3 },
          borderRadius: 2,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography
          variant="h6"
          sx={{
            mb: 2,
            fontWeight: 600,
            color: 'text.primary'
          }}
        >
          {nomeExibicao}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'flex-start' },
            gap: { xs: 1.5, sm: 2 }
          }}
        >
          <TextField
            select
            label="Selecionar Produto"
            value={produtoSelecionado}
            onChange={handleProdutoChange}
            variant="outlined"
            size="small"
            disabled={isLoadingProdutos}
            sx={{
              width: { xs: '100%', sm: 300 },
              maxWidth: '100%',
              bgcolor: 'background.paper',
              borderRadius: 1,
              '& .MuiOutlinedInput-root': { height: CONTROL_H, borderRadius: 1.5, color: 'text.primary' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
              // garante centralização vertical do texto do select
              '& .MuiOutlinedInput-input': { py: 0 },
              '& .MuiSelect-select': { display: 'flex', alignItems: 'center' },
              '& .MuiSelect-icon': { color: 'text.secondary' },
            }}
          >
            <MenuItem value="">
              <em>{isLoadingProdutos ? 'Carregando...' : 'Selecione um produto'}</em>
            </MenuItem>
            {produtos.map((p) => (
              <MenuItem key={p.id} value={p.id}>{p.nome}</MenuItem>
            ))}
          </TextField>

          {canCadastrarProduto && (
            <Button
              variant="contained"
              startIcon={<AddBusinessOutlinedIcon />}
              onClick={handleOpenCadastroProduto}
              sx={{
                height: CONTROL_H,
                textTransform: 'none',
                fontWeight: 600,
                ...primaryButtonSx,
              }}
            >
              Cadastrar Produto
            </Button>
          )}

          {produtoSelecionado && (
            <Button
              variant="outlined"
              startIcon={<ImageIcon />}
              onClick={() => setGaleriaOpen(true)}
              // iguala a altura ao TextField
              sx={{
                height: CONTROL_H,
                width: { xs: '100%', sm: 'auto' },
                borderRadius: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                borderColor: '#1976d2',
                color: '#1976d2',
                '&:hover': {
                  borderColor: '#1565c0',
                  backgroundColor: 'rgba(25, 118, 210, 0.04)',
                },
              }}
            >
              Imagens
            </Button>
          )}
        </Box>

        {/* Validação visual quando nenhum produto estiver selecionado */}
        {!produtoSelecionado && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              borderRadius: 1,
              backgroundColor: 'action.hover',
              border: '1px dashed',
              borderColor: 'divider',
              textAlign: 'center'
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontStyle: 'italic'
              }}
            >
              Selecione um produto para visualizar e editar suas informações
            </Typography>
          </Box>
        )}

        {/* Campo de contexto com loading */}
        {produtoSelecionado && (
          <Box sx={{ mt: 2 }}>
            {isLoadingContexto ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 200,
                  flexDirection: 'column',
                  gap: 2
                }}
              >
                <CircularProgress size={30} />
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Carregando informações do produto...
                </Typography>
              </Box>
            ) : (
              <>
                <TextField
                  multiline
                  rows={20}
                  value={contexto}
                  onChange={(e) => setContexto(e.target.value)}
                  variant="outlined"
                  fullWidth
                  placeholder="Digite as informações específicas deste produto..."
                  disabled={readonly}
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'background.paper',
                      color: 'text.primary',
                      borderRadius: 1.5,
                      '& fieldset': {
                        borderColor: 'divider',
                      },
                      ...(!readonly && {
                        '&:hover fieldset': {
                          borderColor: '#667eea',
                        },
                      }),
                      '&.Mui-focused fieldset': {
                        borderColor: '#667eea',
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#667eea',
                    },
                    '& .MuiInputBase-input': {
                      color: 'text.primary',
                    },
                  }}
                />

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'stretch', sm: 'center' },
                    gap: 2,
                    justifyContent: { xs: 'stretch', sm: 'flex-end' }
                  }}
                >
                  {hasChanges && (
                    <Button
                      variant="outlined"
                      onClick={handleResetChanges}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        width: { xs: '100%', sm: 'auto' },
                        px: 3,
                        py: 1
                      }}
                    >
                      Desfazer
                    </Button>
                  )}

                  {!readonly && (
                    <Button
                      variant="contained"
                      endIcon={<SendIcon />}
                      onClick={handleEnviarContexto}
                      disabled={!shouldEnableButton()}
                      sx={{
                        backgroundColor: '#1976d2',
                        '&:hover': {
                          backgroundColor: '#1565c0',
                        },
                        '&:disabled': {
                          backgroundColor: '#e0e0e0',
                          color: '#9e9e9e',
                        },
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        width: { xs: '100%', sm: 'auto' },
                        px: 3,
                        py: 1
                      }}
                    >
                      {contextoOriginal
                        ? `Atualizar`
                        : `Enviar`
                      }
                    </Button>
                  )}
                </Box>
              </>
            )}
          </Box>
        )}
      </Paper>

      <GaleriaImagensProduto
        open={galeriaOpen}
        onClose={() => setGaleriaOpen(false)}
        produtoNome={produtos.find(p => p.id === produtoSelecionado)?.nome || ''}
        produtoId={produtoSelecionado} // <- ADICIONAR ESTA LINHA
      />

      <ModalCadastroProduto
        open={cadastroProdutoOpen}
        onClose={handleCloseCadastroProduto}
        loadingProdutos={isLoadingProdutos}
        salvandoCadastro={cadastroLoading}
        produtos={produtosParaCadastro}
        produtosCadastrados={produtosCadastrados}
        cadastroSucesso={cadastroSucesso}
        cadastroEmAndamento={cadastroEmAndamento}
        cadastroError={cadastroError}
        idCrm={idCrm}
        onCadastrar={handleCadastrarProduto}
      />

      <ContextoValidation
        open={validationModalOpen}
        onClose={() => setValidationModalOpen(false)}
        nomeContexto={nomeContexto}
        contexto={contexto}
        onConfirm={handleConfirmContexto}
        produto={produto}
        produtoId={produtoSelecionado} // Nova prop
      />
    </>
  );
};

export default ContextoProduto;
